import { HandlersCollection, RefBool } from '@tblabs/truffle';
import { SelectableCollection } from '../../Components/Snapshots/SelectableCollection';
import { Snap } from '../../Models/Snap';
import { BlobPicture } from "../../Models/BlobPicture";
import { ISnapsOnlineStorage } from "./ISnapsOnlineStorage";
import { PhotoRecord } from '../../Models/PhotoRecord';
import { IPhotoStorage } from './IPhotoStorage';
import { OcrState, UploadState } from '../../Models/UploadState';
import { IRemoteOcr } from "../Ocr/IRemoteOcr";


export class SnapStorage
{
    public Items = new SelectableCollection<Snap>();
    public OnOcrReady = new HandlersCollection<(snap: Snap) => void>();
    public AutoSaveOnServer = new RefBool(false).Storable("auto-save-on-server"); // TODO: move to AppConfig or something?
    
    public get WatcherId(): string
    {
        return this._onlineDb.UniqueClientId;
    }

    public get OnlineStorage(): ISnapsOnlineStorage
    {
        return this._onlineDb;
    }

    constructor(private _localDb: IPhotoStorage, private _onlineDb: ISnapsOnlineStorage, private _ocr: IRemoteOcr)
    {
        this.Items.OnRemove(snap => this._localDb.Delete(snap.Item.Id));
    }

    public RemoveAll(): void
    {
        this.Items.RemoveAll();
    }

    public RemoveSelected(): void
    {
        this.Items.Filter(snap => snap.Item.IsSelectedByUser.value).forEach(snap => snap.RemoveSelf());
    }

    public RemoveUnselected(): void
    {
        this.Items.Filter(snap => !snap.Item.IsSelectedByUser.value).forEach(snap => snap.RemoveSelf());
    }

    private async AddToDropbox(snap: Snap): Promise<boolean>
    {
        const uploadResult = await this._onlineDb.Add(snap);
        if (!uploadResult)
        {
            snap.UploadState.value = UploadState.Error;
            return false;
        }

        const flags = { Uploaded: true };
        await this._localDb.UpdateMeta(snap.Id, flags);

        snap.UploadState.value = UploadState.Uploaded;

        return true;
    }

    private CreateSnap(record: PhotoRecord): Snap
    {
        const picture = new BlobPicture(record.Image, record.CreatedAt);
        const snap = new Snap(picture);
        snap.Id = record.Id;
        snap.IsSelectedByUser.value = record.Meta.Selected;
        snap.UploadState.value = record.Meta.Uploaded ? UploadState.Uploaded : UploadState.NotUploaded;
        snap.OcrState.value = record.Meta.OcrText !== null ? OcrState.Ready : OcrState.NoOcr;
        snap.OcrText = record.Meta.OcrText;

        snap.IsSelectedByUser.OnChange(selected =>
        {
            this._localDb.UpdateMeta(snap.Id, { Selected: selected });
        });

        snap.UploadState.OnChange(async (uploadState: UploadState) =>
        {
            if (uploadState === UploadState.StartUploading)
            {
                await this.AddToDropbox(snap);
            }
        });

        snap.OcrState.OnChange(async (ocrState: OcrState) =>
        {
            if (ocrState === OcrState.Ready)
            {
                this.OnOcrReady.Call(snap);
                return;
            }
            else
            if (ocrState === OcrState.Started)
            {
                try
                {
                    const ocrText = await this._ocr.UploadAndScan(snap.Picture.Image);
                
                    if (ocrText !== null)
                    {
                        snap.OcrText = ocrText;
                        await this._localDb.UpdateMeta(snap.Id, { OcrText: ocrText });
                        this.OnOcrReady.Call(snap);
                        snap.OcrState.value = OcrState.Ready;
                    }
                    else
                        snap.OcrState.value = OcrState.Error;
                }
                catch (error)
                {
                    console.error('Error during OCR', error);
                    snap.OcrState.value = OcrState.Error;
                }
            }
        });

        return snap;
    }

    public async Init(): Promise<void>
    {
        let records: PhotoRecord[] = await this._localDb.GetAll();

        records = records.sort((a, b) => +a.CreatedAt - +b.CreatedAt);

        for (const record of records)
        {
            const snap = this.CreateSnap(record);

            this.Items.AddOne(snap);
        }
    }

    public async Add(picture: BlobPicture): Promise<void>
    {
        const photo = new PhotoRecord(picture.Image);
        const snap = this.CreateSnap(photo);
        
        this.Items.AddOne(snap);

        await this._localDb.Add(photo);

        if (this.AutoSaveOnServer.value)
        {
            await this.AddToDropbox(snap);
        }
    }
}
